import Typography, { TypographyProps } from '@ui/components/atoms/typography/typography.atom';
import { clsxm, styled } from '@ui/utils/style.util';
import { ComponentPropsWithRef, ReactNode, memo, useMemo, type JSX } from 'react';

// #region Types
type ListBaseProps = ComponentPropsWithRef<'ul'>;
type ListItemBaseProps = ComponentPropsWithRef<'li'>;

export enum ListItemSizeEnum {
  Default = 'default',
  Large = 'large',
  Small = 'small',
}

export type ListItemSize = `${ListItemSizeEnum}`;

/**
 * Props type for List Container component
 * Extends ul props to allow for all HTML ul attributes
 */
export type ListContainerProps = ListBaseProps;

/**
 * Props type for List Item component
 * @property active - Whether the item is in active state
 * @property contentProps - Props to be passed to the inner container component
 * @property leading - Content to be rendered before the main content
 * @property size - Size variant of the list item
 * @property trailing - Content to be rendered after the main content
 */
export type ListItemProps = ListItemBaseProps & {
  active?: boolean;
  contentAs?: keyof JSX.IntrinsicElements;
  contentClassName?: string;
  contentProps?: TypographyProps;
  label?: string;
  leading?: ReactNode;
  size?: ListItemSize;
  trailing?: ReactNode;
};

export type ListProps = ListBaseProps & {
  items?: ListItemProps[];
};
// #endregion

// #region Constants
const ITEM_SIZE_COMPONENTS = {
  [ListItemSizeEnum.Small]: Typography.BodyTwo,
  [ListItemSizeEnum.Default]: Typography.BodyOne,
  [ListItemSizeEnum.Large]: Typography.HeadingFour,
} as const;
// #endregion

// #region Components
const LeadingTrailingContainer = styled('li', 'h-6 flex items-center');

/**
 * List item component that can contain leading/trailing content
 * Features:
 * - Configurable size (default/small)
 * - Optional active state
 * - Leading and trailing content slots
 * - Customizable inner container
 */
function Item({
  active,
  children,
  className,
  contentAs,
  contentClassName,
  contentProps,
  label,
  leading,
  size = ListItemSizeEnum.Default,
  trailing,
  ...props
}: ListItemProps) {
  const Content = useMemo(() => ITEM_SIZE_COMPONENTS[size], [size]);

  return (
    <li
      {...props}
      className={clsxm(
        'flex items-center bg-white hover:bg-grey-disabled rounded p-2.5 space-x-2 cursor-pointer',
        active && !trailing && 'bg-grey-disabled',
        className,
      )}
    >
      {leading && (
        <LeadingTrailingContainer className="justify-start">{leading}</LeadingTrailingContainer>
      )}

      <Content
        {...contentProps}
        as={contentAs}
        className={clsxm('grow', contentClassName, contentProps?.className)}
      >
        {children ?? label}
      </Content>

      {trailing && (
        <LeadingTrailingContainer className="justify-end">{trailing}</LeadingTrailingContainer>
      )}
    </li>
  );
}
Item.displayName = 'List.Item';

/**
 * Container component for the list
 * Used for wrapping list items with consistent styling
 */
function Container({ className, ...props }: ListContainerProps) {
  return (
    <ul
      {...props}
      className={clsxm('flex flex-col shadow-card p-2.5 rounded-[5px] bg-white-snow', className)}
    />
  );
}
Container.displayName = 'List.Container';

function BaseList({ items, ...props }: ListProps) {
  // #region Computed Values
  const renderedItems = useMemo(
    () => items?.map((item) => <Item key={item.id} {...item} />),
    [items],
  );
  // #endregion

  return <Container {...props}>{renderedItems}</Container>;
}
BaseList.displayName = 'List';
// #endregion

// Export all list components as a single object
const List = Object.assign(memo(BaseList), {
  Container: memo(Container),
  Item: memo(Item),
});

export default List;
